import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { Field, Input, Select, Textarea, FormGrid, SectionLabel, SaveMessage, Card, CardHeader, SubmitBtn, EmptyState, Badge, ENTITY_LABELS } from './Shared';

const ENTITIES = Object.keys(ENTITY_LABELS).map(k => ({ value: k, label: ENTITY_LABELS[k] }));
const ENTRY_TYPES = [
  { value: 'advance', label: 'Advance (lender sends money to borrower)' },
  { value: 'repayment', label: 'Repayment (borrower pays lender back)' },
  { value: 'interest', label: 'Interest accrued' },
  { value: 'adjustment', label: 'Adjustment (correction — negative reduces the balance)' }
];
const TYPE_LABELS = { advance: 'Advance', repayment: 'Repayment', interest: 'Interest', adjustment: 'Adjustment' };
const TYPE_VARIANTS = { advance: 'blue', repayment: 'green', interest: 'amber', adjustment: 'gray' };

const money = n => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const today = () => new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD in local time
const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const emptyForm = () => ({
  lender: 'national', borrower: 'academy', entry_type: 'advance', amount: '', entry_date: today(),
  interest_rate_pct: '', due_date: '', terms: '', memo: '', qbo_reference: ''
});

export default function IntercompanyPage({ profile }) {
  const [entries, setEntries] = useState([]);
  const [balances, setBalances] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [msg, setMsg] = useState({ show: false, text: '', error: false });

  useEffect(() => { load(); }, []);

  async function load() {
    const [e, b] = await Promise.all([
      supabase.from('intercompany_ledger').select('*').order('entry_date', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('intercompany_balances').select('*')
    ]);
    const err = e.error || b.error;
    setLoadError(err ? err.message : '');
    setEntries(e.data || []);
    setBalances(b.data || []);
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function save() {
    const fail = text => setMsg({ show: true, text, error: true });
    const amt = parseFloat(form.amount);
    if (form.lender === form.borrower) return fail('Lender and borrower must be different.');
    if (!form.amount || isNaN(amt) || amt === 0) return fail('Enter an amount.');
    if (form.entry_type !== 'adjustment' && amt < 0) return fail('Amount must be positive. To reduce a balance, use a repayment or an adjustment.');
    if (!form.entry_date) return fail('Pick a date.');
    const rate = form.interest_rate_pct === '' ? null : parseFloat(form.interest_rate_pct);
    if (rate !== null && isNaN(rate)) return fail('Interest rate must be a number.');

    setLoading(true);
    const { error } = await supabase.from('intercompany_ledger').insert({
      lender: form.lender,
      borrower: form.borrower,
      entry_type: form.entry_type,
      amount: amt,
      entry_date: form.entry_date,
      interest_rate_pct: rate,
      due_date: form.due_date || null,
      terms: form.terms.trim() || null,
      memo: form.memo.trim() || null,
      qbo_reference: form.qbo_reference.trim() || null,
      created_by: profile.id
    });
    setLoading(false);
    if (error) return fail('Error: ' + error.message);
    setMsg({ show: true, text: 'Entry recorded.', error: false });
    setForm(f => ({ ...emptyForm(), lender: f.lender, borrower: f.borrower }));
    load();
    setTimeout(() => setMsg({ show: false }), 3000);
  }

  const outstanding = balances.reduce((s, b) => s + Math.max(0, Number(b.balance_owed) || 0), 0);

  return (
    <div>
      <div style={{ fontSize: '13px', color: '#64748B', marginBottom: '16px', lineHeight: '1.6' }}>
        Tracks money one entity advances to another so it can be repaid later. Entries cannot be edited or deleted —
        fix a mistake by adding an adjustment (a negative amount reduces the balance). Admin only.
      </div>

      {loadError && (
        <Card>
          <div style={{ color: '#B91C1C', fontSize: '13px' }}>
            Could not load the ledger: {loadError}. If this says a table does not exist, run migrations/002_core_network_tables.sql in Supabase.
          </div>
        </Card>
      )}

      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-label">Total outstanding</div>
          <div className="metric-value red">{money(outstanding)}</div>
          <div className="metric-sub">owed between entities</div>
        </div>
        {balances.map(b => (
          <div key={b.lender + b.borrower} className="metric-card">
            <div className="metric-label">{ENTITY_LABELS[b.borrower] || b.borrower} owes {ENTITY_LABELS[b.lender] || b.lender}</div>
            <div className="metric-value blue">{money(b.balance_owed)}</div>
            <div className="metric-sub">last entry {fmtDate(b.last_entry)}</div>
          </div>
        ))}
      </div>

      <Card>
        <SectionLabel>RECORD AN ENTRY</SectionLabel>
        <FormGrid>
          <Field label="Lender (money comes from)"><Select value={form.lender} onChange={set('lender')} options={ENTITIES} /></Field>
          <Field label="Borrower (money goes to)"><Select value={form.borrower} onChange={set('borrower')} options={ENTITIES} /></Field>
          <Field label="Entry type"><Select value={form.entry_type} onChange={set('entry_type')} options={ENTRY_TYPES} /></Field>
          <Field label="Amount (USD)"><Input type="number" value={form.amount} onChange={set('amount')} placeholder="0.00" /></Field>
          <Field label="Date"><Input type="date" value={form.entry_date} onChange={set('entry_date')} /></Field>
          <Field label="Interest rate % (optional)"><Input type="number" value={form.interest_rate_pct} onChange={set('interest_rate_pct')} placeholder="e.g. 6" /></Field>
          <Field label="Repay by (optional)"><Input type="date" value={form.due_date} onChange={set('due_date')} /></Field>
          <Field label="QuickBooks reference (optional)"><Input value={form.qbo_reference} onChange={set('qbo_reference')} placeholder="transaction or journal #" /></Field>
          <Field label="Memo"><Input value={form.memo} onChange={set('memo')} placeholder="what this was for" /></Field>
          <Field label="Terms" full><Textarea value={form.terms} onChange={set('terms')} placeholder="Repayment terms, e.g. repay from first Judgr revenue; interest at AFR" /></Field>
        </FormGrid>
        <div className="form-actions">
          <SubmitBtn onClick={save} loading={loading}>Record entry</SubmitBtn>
        </div>
        <SaveMessage {...msg} />
      </Card>

      <Card>
        <CardHeader title="Ledger" hint={entries.length + ' entries'} />
        {!entries.length ? <EmptyState text="No entries yet. Record what Redline has advanced so far." /> : (
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Lender → Borrower</th><th>Type</th><th style={{ textAlign: 'right' }}>Amount</th><th>Details</th></tr>
            </thead>
            <tbody>
              {entries.map(r => (
                <tr key={r.id}>
                  <td>{fmtDate(r.entry_date)}</td>
                  <td>{ENTITY_LABELS[r.lender] || r.lender} → {ENTITY_LABELS[r.borrower] || r.borrower}</td>
                  <td><Badge variant={TYPE_VARIANTS[r.entry_type] || 'gray'}>{TYPE_LABELS[r.entry_type] || r.entry_type}</Badge></td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{money(r.amount)}</td>
                  <td style={{ fontSize: '12px', color: '#64748B' }}>
                    {[r.memo, r.terms, r.interest_rate_pct != null ? r.interest_rate_pct + '% interest' : '', r.due_date ? 'repay by ' + fmtDate(r.due_date) : '', r.qbo_reference ? 'QBO ' + r.qbo_reference : '']
                      .filter(Boolean).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
